import React, { useRef, useEffect, useState } from 'react';

interface OTPInputProps {
    length?: number;
    value: string;
    onChange: (value: string) => void;
    onComplete?: (value: string) => void;
    error?: string;
    disabled?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
    length = 4,
    value,
    onChange,
    onComplete,
    error,
    disabled = false
}) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [values, setValues] = useState<string[]>(Array(length).fill(''));

    useEffect(() => {
        // Синхронизируем внутреннее состояние с внешним значением
        const newValues = value.split('').concat(Array(length).fill('')).slice(0, length);
        setValues(newValues);
    }, [value, length]);

    useEffect(() => {
        // Фокусируемся на первом пустом поле при монтировании
        const firstEmptyIndex = values.findIndex(val => val === '');
        const targetIndex = firstEmptyIndex === -1 ? length - 1 : firstEmptyIndex;
        inputRefs.current[targetIndex]?.focus();
    }, []);

    const handleChange = (index: number, inputValue: string) => {
        // Разрешаем только цифры
        const digit = inputValue.replace(/\D/g, '').slice(-1);

        const newValues = [...values];
        newValues[index] = digit;
        setValues(newValues);

        const newValue = newValues.join('');
        onChange(newValue);

        // Автоматически переходим к следующему полю
        if (digit && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        // Вызываем onComplete если все поля заполнены
        if (newValue.length === length && onComplete) {
            onComplete(newValue);
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !values[index] && index > 0) {
            // Переходим к предыдущему полю при Backspace
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, length);

        const newValues = Array(length).fill('');
        for (let i = 0; i < pastedData.length; i++) {
            newValues[i] = pastedData[i];
        }

        setValues(newValues);
        onChange(pastedData);

        // Фокусируемся на последнем заполненном поле
        const lastFilledIndex = Math.min(pastedData.length - 1, length - 1);
        inputRefs.current[lastFilledIndex]?.focus();

        if (pastedData.length === length && onComplete) {
            onComplete(pastedData);
        }
    };

    return (
        <div className="space-y-2">
            <div className="flex justify-center space-x-2">
                {Array.from({ length }, (_, index) => (
                    <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={values[index] || ''}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        disabled={disabled}
                        className={`
              w-12 h-12 text-center text-xl font-semibold border-2 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
              transition-colors duration-200
              ${error
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300'
                            }
              ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}
            `}
                    />
                ))}
            </div>
            {error && <p className="text-center form-error">{error}</p>}
        </div>
    );
};
